/**
 * /{ws}/later — Slack's Later (core LaterViewModel, logic/later): saved messages and reminders in three tabs (In
 * progress / Archived / Completed). A row opens the message in context; its actions complete it, set or snooze its
 * reminder, archive it, move it back, or remove it. Overdue reminders are flagged.
 */
import type { LaterState } from "@bundle/contracts";
import type { LaterRow, LaterViewModel } from "@bundle/core";
import { Later, Profile } from "@bundle/core";
import { Avatar, Button, ComingSoon, focusMessage, Icon, IconButton, Menu, Pressable, ScrollView, SegmentedControl, Spinner, Stack, Text, useFormat, useNavigation, useT, useViewModel, useWorkspace, useLater, openRemind, type MenuEntry } from "@bundle/ui";
import { useEffect } from "react";

const TAB_KEY = { in_progress: "inProgress", archived: "archived", completed: "completed" } as const;

export function LaterScreen() {
  const t = useT();
  const later = useLater();
  if (later === undefined) return <ComingSoon icon="bookmark" title={t("later.title")} body={t("later.empty.inProgress")} />;
  return <LaterView vm={later} />;
}

function LaterView({ vm }: { vm: LaterViewModel }) {
  const t = useT();
  const s = useViewModel(vm);
  // Opening Later takes the server's latest (reminders that fired, items saved on other devices).
  useEffect(() => void vm.refresh(), [vm]);

  const tabs = Later.LATER_TABS.map((tab) => ({ value: tab, label: t(`later.tab.${TAB_KEY[tab]}`), count: s.counts[tab] || undefined }));
  return (
    <Stack grow testID="later-screen">
      <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="8" paddingY="4">
        <Text variant="title2" weight="bold">
          {t("later.title")}
        </Text>
      </Stack>
      <Stack paddingX="8" paddingY="1">
        <SegmentedControl<LaterState> options={tabs} value={s.tab} onChange={(tab) => vm.setTab(tab)} variant="pills" size="sm" accessibilityLabel={t("later.tabs")} testID="later-tabs" />
      </Stack>
      {s.status === "loading" ? (
        <Stack grow align="center" justify="center" testID="later-loading">
          <Spinner />
        </Stack>
      ) : s.status === "failed" ? (
        <Stack grow align="center" justify="center" gap="3" padding="8" testID="later-failed">
          <Text color="text.secondary" align="center">
            {t("later.failed")}
          </Text>
          <Button variant="secondary" onPress={() => vm.retry()} label={t("later.retry")} testID="later-retry" />
        </Stack>
      ) : s.rows.length === 0 ? (
        <Stack grow align="center" justify="center" padding="8" testID="later-empty">
          <Icon name={s.tab === "completed" ? "checkCircle" : s.tab === "archived" ? "archive" : "bookmark"} size="icon.lg" color="text.tertiary" />
          <Text color="text.secondary" align="center">
            {t(`later.empty.${TAB_KEY[s.tab]}`)}
          </Text>
        </Stack>
      ) : (
        <ScrollView>
          <Stack paddingX="5" paddingY="2" gap="1">
            {s.rows.map((row) => (
              <LaterRowView key={row.id} vm={vm} row={row} />
            ))}
          </Stack>
        </ScrollView>
      )}
    </Stack>
  );
}

function LaterRowView({ vm, row }: { vm: LaterViewModel; row: LaterRow }) {
  const t = useT();
  const fmt = useFormat();
  const nav = useNavigation();
  const ws = useWorkspace();
  const item = row.item;
  const author = row.authorId === undefined ? undefined : ws.state.cache?.members[row.authorId];
  const look = author === undefined ? {} : Profile.avatarLook(author);
  const open = () => {
    if (row.url === undefined) return;
    nav.open(row.url);
    if (item.messageId !== undefined) focusMessage(item.messageId);
  };
  const reminderLabel =
    row.forConversation !== undefined
      ? t("later.forConversation", { conversation: row.forConversation })
      : row.fromName !== undefined
        ? t("later.fromPerson", { name: row.fromName })
        : t("later.textReminder");
  const more: MenuEntry[] = [];
  if (item.state === "in_progress") more.push({ id: "archive", label: t("later.archive"), icon: "archive", onSelect: () => vm.archive(item.id) });
  else more.push({ id: "in-progress", label: t("later.moveToInProgress"), icon: "bookmark", onSelect: () => vm.moveToInProgress(item.id) });
  if (item.repeat !== undefined) more.push({ id: "stop-repeat", label: t("later.stopRepeat"), icon: "bellOff", onSelect: () => vm.stopRepeat(item.id) });
  if (item.remindAt !== undefined && item.state === "in_progress") more.push({ id: "clear-reminder", label: t("later.reminder.clear"), icon: "bellOff", onSelect: () => vm.clearReminder(item.id) });
  more.push({ id: "sep", separator: true }, { id: "remove", label: t("later.remove"), icon: "trash", destructive: true, onSelect: () => vm.remove(item.id) });

  return (
    <Stack direction="horizontal" align="start" gap="3" paddingX="3" paddingY="2" radius="md" background={row.overdue ? "bg.selected" : undefined} testID={`later-row-${row.id}`}>
      {row.url === undefined ? (
        <Stack direction="horizontal" grow>
        <Stack direction="horizontal" align="start" gap="3" grow>
          {row.isText ? (
            <Stack align="center" justify="center" testID={`later-bell-${row.id}`}>
              <Icon name="clock" size="icon.lg" color="text.secondary" />
            </Stack>
          ) : (
            <Avatar name={row.authorName ?? "?"} src={look.src} tone={look.tone} emoji={look.emoji} size="avatar.md" />
          )}
          <Stack grow gap="0.5">
            <Stack direction="horizontal" align="center" gap="2" wrap>
              <Text variant="footnote" color="text.secondary">
                {row.isText ? reminderLabel : row.conversation}
              </Text>
              {item.repeat === undefined ? null : (
                <Text variant="footnote" color="text.secondary" testID={`later-repeats-${row.id}`}>
                  {t("later.repeats")}
                </Text>
              )}
              {item.remindAt === undefined || item.state !== "in_progress" ? null : (
                <Text variant="footnote" weight="semibold" color={row.overdue ? "status.danger.fg" : "accent.text"} testID={`later-due-${row.id}`}>
                  {t(row.overdue ? "later.overdue" : "later.due", { time: fmt.timestamp(item.remindAt) })}
                </Text>
              )}
            </Stack>
            {row.authorName === undefined ? null : (
              <Text weight="semibold" lines={1}>
                {row.authorName}
              </Text>
            )}
            <Text color={row.gone ? "text.tertiary" : undefined} lines={3}>
              {row.gone ? t("later.messageGone") : row.preview}
            </Text>
          </Stack>
        </Stack>
        </Stack>
      ) : (
        <Pressable onPress={open} direction="horizontal" grow accessibilityLabel={row.preview} testID={`later-open-${row.id}`}>
        <Stack direction="horizontal" align="start" gap="3" grow>
          {row.isText ? (
            <Stack align="center" justify="center" testID={`later-bell-${row.id}`}>
              <Icon name="clock" size="icon.lg" color="text.secondary" />
            </Stack>
          ) : (
            <Avatar name={row.authorName ?? "?"} src={look.src} tone={look.tone} emoji={look.emoji} size="avatar.md" />
          )}
          <Stack grow gap="0.5">
            <Stack direction="horizontal" align="center" gap="2" wrap>
              <Text variant="footnote" color="text.secondary">
                {row.isText ? reminderLabel : row.conversation}
              </Text>
              {item.repeat === undefined ? null : (
                <Text variant="footnote" color="text.secondary" testID={`later-repeats-${row.id}`}>
                  {t("later.repeats")}
                </Text>
              )}
              {item.remindAt === undefined || item.state !== "in_progress" ? null : (
                <Text variant="footnote" weight="semibold" color={row.overdue ? "status.danger.fg" : "accent.text"} testID={`later-due-${row.id}`}>
                  {t(row.overdue ? "later.overdue" : "later.due", { time: fmt.timestamp(item.remindAt) })}
                </Text>
              )}
            </Stack>
            {row.authorName === undefined ? null : (
              <Text weight="semibold" lines={1}>
                {row.authorName}
              </Text>
            )}
            <Text color={row.gone ? "text.tertiary" : undefined} lines={3}>
              {row.gone ? t("later.messageGone") : row.preview}
            </Text>
          </Stack>
        </Stack>
        </Pressable>
      )}
      <Stack direction="horizontal" align="center" gap="1">
        {item.state === "completed" ? null : (
          <IconButton icon="check" accessibilityLabel={t("later.complete")} onPress={() => vm.complete(item.id)} testID={`later-complete-${row.id}`} />
        )}
        <IconButton icon="clock" accessibilityLabel={t("later.remind")} onPress={() => openRemind({ kind: "item", id: item.id })} testID={`later-remind-${row.id}`} />
        <Menu items={more} align="end" testID={`later-menu-${row.id}`}>
          <IconButton icon="more" accessibilityLabel={t("later.more")} onPress={() => undefined} testID={`later-more-${row.id}`} />
        </Menu>
      </Stack>
    </Stack>
  );
}
